<template>
  <div class="tree-view">
    <div class="box">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h4 class="title is-4">{{ $t("expedient browser") }}</h4>
          </div>
        </div>
      </div>

      <div class="columns">
        <div class="column is-one-quarter">
          <div class="field has-addons">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="searchQuery"
                :placeholder="$t('archive.search_tree')"
                @input="filterTree"
              />
            </div>
            <div class="control">
              <button
                class="button is-info"
                @click="refresh_tree"
                :data-tooltip="$t('archive.refresh')"
                data-tooltip-position="top"
              >
                <span class="icon">
                  <i class="fa fa-refresh"></i>
                </span>
              </button>
            </div>
            <!-- <div class="control">
              <button class="button is-small is-warning" @click="collapseAll">
                <span class="icon">
                  <i class="fa fa-compress"></i>
                </span>
              </button>
            </div> -->
          </div>
          <!-- Tree Structure -->
          <div class="tree-container">
            <div v-if="loading" class="has-text-centered">
              <div class="loader-wrapper">
                <div class="loader"></div>
                <p>{{ $t("archive.loading_tree") }}</p>
              </div>
            </div>

            <div
              v-else-if="filteredCustomers.length === 0"
              class="has-text-centered"
            >
              <div class="content">
                <p class="has-text-grey">
                  <i class="fa fa-search fa-3x"></i>
                </p>
                <p>
                  {{
                    searchQuery
                      ? $t("archive.no_search_results")
                      : $t("archive.no_customers_found")
                  }}
                </p>
              </div>
            </div>

            <div v-else class="tree-list">
              <!-- Customer Level -->
              <div
                v-for="customer in filteredCustomers"
                :key="customer.uuid"
                class="tree-node customer-node"
              >
                <div
                  class="tree-item"
                  @click="toggleCustomer(customer)"
                  :class="{
                    'is-expanded': expandedCustomers.has(customer.uuid),
                  }"
                >
                  <span class="tree-icon">
                    <i
                      class="fa"
                      :class="{
                        'fa-chevron-right': !expandedCustomers.has(
                          customer.uuid
                        ),
                        'fa-chevron-down': expandedCustomers.has(customer.uuid),
                      }"
                    ></i>
                  </span>

                  <span class="tree-icon">
                    <i class="fa fa-user has-text-info"></i>
                  </span>

                  <span class="tree-label">{{ customer.name }}</span>

                  <span class="tree-count tag is-info is-small">
                    {{ customer.expedient_count || 0 }}
                  </span>
                </div>

                <!-- Expedients Level -->
                <div
                  v-if="expandedCustomers.has(customer.uuid)"
                  class="tree-children"
                >
                  <div
                    v-if="
                      customer.expedients && customer.expedients.length === 0
                    "
                    class="tree-empty"
                  >
                    <span class="has-text-grey">{{
                      $t("archive.no_expedients")
                    }}</span>
                  </div>

                  <div
                    v-for="expedient in customer.expedients"
                    :key="expedient.uuid"
                    class="tree-node expedient-node"
                  >
                    <div
                      class="tree-item"
                      @click="toggleExpedient(expedient)"
                      :class="{
                        'is-expanded': expandedExpedients.has(expedient.uuid),
                        'is-selected':
                          selectedExpedient &&
                          selectedExpedient.uuid === expedient.uuid,
                      }"
                    >
                      <!-- <span class="tree-icon">
                        <i
                          class="fa"
                          :class="{
                            'fa-chevron-right': !expandedExpedients.has(
                              expedient.uuid
                            ),
                            'fa-chevron-down': expandedExpedients.has(
                              expedient.uuid
                            ),
                          }"
                        ></i>
                      </span> -->

                      <span class="tree-icon">
                        <i class="fa fa-folder has-text-primary"></i>
                      </span>

                      <span class="tree-label">{{ expedient.name }}</span>

                      <span class="tree-count tag is-primary is-small">
                        {{ expedient.file_count || 0 }}
                      </span>

                      <!-- <span
                        class="tree-status tag is-small"
                        :class="{
                          'is-success': expedient.status === 'active',
                          'is-warning': expedient.status === 'pending',
                          'is-danger': expedient.status === 'closed',
                          'is-dark': expedient.status === 'archived',
                        }"
                      >
                        {{ $t("archive.status_" + expedient.status) }}
                      </span> -->
                    </div>
                  </div>

                  <!-- Add new -->
                  <div class="tree-node expedient-node new-expedient-node">
                    <div class="tree-item" @click="toggleExpedient(expedient)">
                      <span class="tree-icon">
                        <i class="fa fa-plus has-text-info"></i>
                      </span>
                      <span class="tree-label">{{
                        $t("archive.new expedient")
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="column">
          <expedient-viewer ref="expedient_viewer" />
        </div>
      </div>
    </div>

    <!-- Right-click Context Menu -->
    <div
      v-if="contextMenu.show"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      @click="hideContextMenu"
    >
      <div class="dropdown is-active">
        <div class="dropdown-menu">
          <div class="dropdown-content">
            <a
              v-if="contextMenu.type === 'customer'"
              class="dropdown-item"
              @click="manageCustomer"
            >
              <span class="icon"><i class="fa fa-edit"></i></span>
              {{ $t("archive.manage_customer") }}
            </a>
            <a
              v-if="contextMenu.type === 'expedient'"
              class="dropdown-item"
              @click="manageExpedient"
            >
              <span class="icon"><i class="fa fa-folder-open"></i></span>
              {{ $t("archive.manage_expedient") }}
            </a>
            <a
              v-if="contextMenu.type === 'expedient'"
              class="dropdown-item"
              @click="uploadToExpedient"
            >
              <span class="icon"><i class="fa fa-upload"></i></span>
              {{ $t("archive.upload_file") }}
            </a>
            <a
              v-if="contextMenu.type === 'file'"
              class="dropdown-item"
              @click="viewFileFromContext"
            >
              <span class="icon"><i class="fa fa-eye"></i></span>
              {{ $t("archive.view_file") }}
            </a>
            <a
              v-if="contextMenu.type === 'file'"
              class="dropdown-item"
              @click="downloadFileFromContext"
            >
              <span class="icon"><i class="fa fa-download"></i></span>
              {{ $t("archive.download_file") }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <file-upload-modal ref="file_upload_modal" />
  </div>
</template>

<script>
import { get, success_message, warning_message, error_message } from "@/utils";
import FileUploadModal from "./file_upload_modal/Index.vue";
import ExpedientViewer from "./tree_view_components/ExpedientViewer.vue";

export default {
  name: "TreeView",
  components: { FileUploadModal, ExpedientViewer },
  data() {
    return {
      customers: [],
      filteredCustomers: [],
      expandedCustomers: new Set(),
      expandedExpedients: new Set(),
      loadingFiles: new Set(),
      loading: false,
      searchQuery: "",
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        type: "",
        item: null,
      },
      selectedExpedient: null,
    };
  },
  mounted() {
    this.loadTreeData();
    document.addEventListener("click", this.hideContextMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.hideContextMenu);
  },
  methods: {
    async loadTreeData() {
      this.$root.loader(true);
      this.loading = true;
      try {
        const data = await get("/archive/customer/tree/datatables", {
          start: 0,
          length: 1000, // Get all customers
          search: { value: this.searchQuery || "" },
        });
        this.customers = data || [];
        this.filteredCustomers = [...this.customers];
      } catch (error) {
        console.error("Error loading tree data:", error);
        error_message(this.$t("archive.error_loading_tree"));
      } finally {
        this.loading = false;
        this.$root.loader(false);
      }
    },

    refresh_tree() {
      this.loadTreeData();
    },

    async toggleCustomer(customer) {
      if (this.expandedCustomers.has(customer.uuid)) {
        this.expandedCustomers.delete(customer.uuid);
        // Also collapse all expedients under this customer
        if (customer.expedients) {
          customer.expedients.forEach((exp) => {
            this.expandedExpedients.delete(exp.uuid);
          });
        }
      } else {
        this.expandedCustomers.add(customer.uuid);
        if (!customer.expedients) {
          await this.loadExpedients(customer);
        }
      }
    },

    async toggleExpedient(expedient) {
      this.selectedExpedient = expedient;
      this.$refs.expedient_viewer.set_expedient(expedient.uuid);
      // if (this.expandedExpedients.has(expedient.uuid)) {
      //   this.expandedExpedients.delete(expedient.uuid);
      // } else {
      //   this.expandedExpedients.add(expedient.uuid);
      //   if (!expedient.files) {
      //     await this.loadFiles(expedient);
      //   }
      // }
    },

    async loadExpedients(customer) {
      try {
        const data = await get("/archive/expedient/datatables", {
          start: 0,
          length: 1000,
          search: { value: "" },
          customer_uuid: customer.uuid,
        });
        customer.expedients = data || [];
      } catch (error) {
        console.error("Error loading expedients:", error);
        error_message(this.$t("archive.error_loading_expedients"));
      }
    },

    async loadFiles(expedient) {
      this.loadingFiles.add(expedient.uuid);
      try {
        const data = await get("/archive/file/datatables", {
          start: 0,
          length: 1000,
          search: { value: "" },
          expedient_uuid: expedient.uuid,
        });
        expedient.files = data || [];
      } catch (error) {
        console.error("Error loading files:", error);
        error_message(this.$t("archive.error_loading_files"));
      } finally {
        this.loadingFiles.delete(expedient.uuid);
      }
    },

    filterTree() {
      this.loadTreeData();
      // if (!this.searchQuery.trim()) {
      //   this.filteredCustomers = [...this.customers];
      //   return;
      // }
      // const query = this.searchQuery.toLowerCase();
      // this.filteredCustomers = this.customers.filter((customer) => {
      //   // Check customer name
      //   if (customer.name.toLowerCase().includes(query)) {
      //     return true;
      //   }
      //   // Check expedient names
      //   if (customer.expedients) {
      //     return customer.expedients.some((exp) =>
      //       exp.name.toLowerCase().includes(query)
      //     );
      //   }
      //   return false;
      // });
    },

    expandAll() {
      this.customers.forEach((customer) => {
        this.expandedCustomers.add(customer.uuid);
        if (customer.expedients) {
          customer.expedients.forEach((exp) => {
            this.expandedExpedients.add(exp.uuid);
          });
        }
      });
    },

    collapseAll() {
      this.expandedCustomers.clear();
      this.expandedExpedients.clear();
    },

    showContextMenu(event, type, item) {
      event.preventDefault();
      this.contextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        type: type,
        item: item,
      };
    },

    hideContextMenu() {
      this.contextMenu.show = false;
    },

    viewFile(file) {
      this.$emit("view-file", file);
    },

    async downloadFile(file) {
      try {
        const response = await get(
          `/archive/file/download?uuid=${file.uuid}`,
          null,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.original_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading file:", error);
        error_message(this.$t("archive.error_downloading_file"));
      }
    },

    uploadFile(expedient) {
      // this.$emit("upload-file", expedient);
      this.$refs.file_upload_modal.show_modal(expedient.uuid);
    },

    manageCustomer() {
      this.$router.push({ name: "archive-customers" });
      this.hideContextMenu();
    },

    manageExpedient() {
      this.$router.push({ name: "archive-expedients" });
      this.hideContextMenu();
    },

    uploadToExpedient() {
      this.uploadFile(this.contextMenu.item);
      this.hideContextMenu();
    },

    viewFileFromContext() {
      this.viewFile(this.contextMenu.item);
      this.hideContextMenu();
    },

    downloadFileFromContext() {
      this.downloadFile(this.contextMenu.item);
      this.hideContextMenu();
    },

    getFileIcon(file) {
      let fileType = file.drive_file.settings.mimetype || "unknown";
      const iconMap = {
        pdf: "fa fa-file-pdf-o has-text-danger",
        doc: "fa fa-file-word has-text-info",
        docx: "fa fa-file-word has-text-info",
        xls: "fa fa-file-excel has-text-success",
        xlsx: "fa fa-file-excel has-text-success",
        ppt: "fa fa-file-powerpoint has-text-warning",
        pptx: "fa fa-file-powerpoint has-text-warning",
        txt: "fa fa-file-alt has-text-grey",
        jpg: "fa fa-file-image has-text-primary",
        jpeg: "fa fa-file-image has-text-primary",
        png: "fa fa-file-image has-text-primary",
        gif: "fa fa-file-image has-text-primary",
        zip: "fa fa-file-archive has-text-dark",
        rar: "fa fa-file-archive has-text-dark",
        mp3: "fa fa-file-audio has-text-info",
        mp4: "fa fa-file-video has-text-info",
        csv: "fa fa-file-csv has-text-success",
        unknown: "fa fa-file has-text-grey-light",
      };
      // Extract main type from mimetype (e.g., "application/pdf" -> "pdf")
      let mainType = fileType.split("/").pop().toLowerCase();
      return iconMap[mainType] || iconMap["unknown"];
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";

      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.tree-view {
  padding: 20px;
}

.tree-container {
  max-height: 70vh;
  overflow-y: auto;
  /* border: 1px solid #dbdbdb; */
  border-radius: 6px;
  padding: 10px;
}

.tree-node {
  margin: 2px 0;
}

.tree-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.tree-item:hover {
  background-color: #292929;
}

.tree-item.is-expanded {
  background-color: #2c2c2c;
}

.tree-item.is-selected {
  background-color: #3273dc;
  color: white;
}

.customer-node .tree-item {
  font-weight: 600;
  border-left: 4px solid #3273dc;
}

.expedient-node .tree-item {
  font-weight: 500;
  margin-left: 20px;
  border-left: 2px solid #48c774;
}

.file-node .tree-item {
  margin-left: 40px;
  font-size: 0.9rem;
}

.tree-icon {
  width: 20px;
  display: flex;
  justify-content: center;
  margin-right: 8px;
}

.tree-label {
  flex: 1;
  margin-right: 10px;
}

.tree-count,
.tree-status,
.tree-size {
  margin-left: 5px;
}

.tree-actions {
  display: flex;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.tree-item:hover .tree-actions {
  opacity: 1;
}

.new-expedient-node .tree-item {
  font-style: italic;
  color: #3273dc;
  border-left: 2px dashed #3273dc;
}

.tree-children {
  margin-left: 20px;
  border-left: 1px dotted #dbdbdb;
}

.tree-empty,
.tree-loading {
  padding: 10px 20px;
  color: #666;
  font-style: italic;
}

.loader-wrapper {
  padding: 40px;
}

.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3273dc;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.context-menu {
  position: fixed;
  z-index: 9999;
}

.context-menu .dropdown-menu {
  position: static;
  display: block;
}

.context-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
